import { defineStore } from "pinia"
import { type Category } from "~/models/category"
import { type ApiResponse } from "~/types/api"

export const useCategoryStore = defineStore("category", () => {
	const app = useNuxtApp()

	const state = {
		categories: ref<Category[] | null>(null)
	}

	const productOptions = computed(
		() =>
			state.categories.value
				?.filter((c) => c.type == "product-options" && !c.parent_id)
				?.map((c) => ({
					parent: { ...c, label: c.title[app.$i18n.locale] || c.title.en },
					children:
						state.categories.value
							?.filter((ch) => ch.type == "product-options" && ch.parent_id == c.id)
							?.map((ch) => ({
								...ch,
								label: ch.title[app.$i18n.locale] || ch.title.en
							})) || []
				})) || []
	)

	const createCategory = async (formData: CreateCategoryRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<CreateCategoryResponse>(CreateCategory.url(), {
			...useFetchConfig(),
			method: CreateCategory.method,
			body: formData
		})
		if (state.categories.value) {
			state.categories.value = [...state.categories.value, data.data]
			if (
				companyStore.company?.id &&
				companyStore.company.id == data.data.company_id &&
				data.data.type == "organization-unit" &&
				companyStore.company.organization_units
			) {
				companyStore.company.organization_units = [...companyStore.company.organization_units, data.data]
			}
		}
		return data.data
	}

	const editCategory = async (formData: EditCategoryRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<EditCategoryResponse>(EditCategory.url(), {
			...useFetchConfig(),
			method: EditCategory.method,
			body: formData
		})
		if (state.categories.value) {
			state.categories.value = state.categories.value.map((v) => (v.id === data.data.id ? data.data : v))
			if (
				companyStore.company?.id &&
				companyStore.company.id == data.data.company_id &&
				data.data.type == "organization-unit" &&
				companyStore.company.organization_units
			) {
				companyStore.company.organization_units = companyStore.company.organization_units.map((v) =>
					v.id === data.data.id ? data.data : v
				)
			}
		}
		return data.data
	}

	const deleteCategory = async (formData: DeleteCategoryRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<DeleteCategoryResponse>(DeleteCategory.url(formData.id), {
			...useFetchConfig(),
			method: DeleteCategory.method,
			body: formData
		})
		if (state.categories.value) {
			state.categories.value = state.categories.value.filter((v) => v.id !== formData.id)
			if (
				companyStore.company?.id &&
				companyStore.company.id == data.data.company_id &&
				data.data.type == "organization-unit" &&
				companyStore.company.organization_units
			) {
				companyStore.company.organization_units = companyStore.company.organization_units.filter(
					(v) => v.id !== formData.id
				)
			}
		}
		return data.data
	}

	const searchCategories = async (formData: SearchCategoriesRequest) => {
		const data = await $fetch<SearchCategoriesResponse>(SearchCategories.url(), {
			...useFetchConfig(),
			method: SearchCategories.method,
			query: formData
		})
		return data.data
	}

	const getCategory = async (id: number) => {
		const data = await $fetch<GetCategoryResponse>(GetCategory.url(id), {
			...useFetchConfig(),
			method: GetCategory.method
		})
		return data.data
	}

	return {
		...state,
		productOptions,
		createCategory,
		editCategory,
		deleteCategory,
		searchCategories,
		getCategory
	}
})

// category :: create category
export interface CreateCategoryRequest {
	company_id: number | null
	parent_id?: number | null
	title_en?: string
	title_tr?: string
	title_fa?: string
	content_en?: string
	content_tr?: string
	content_fa?: string
	staff_number_prefix?: string
	file_id?: number | null
	image_id?: number | null
	mobile_image_id?: number | null
	background_image_id?: number | null
	mobile_background_image_id?: number | null
	type: string
	slug: string
	color?: string | null
}
export interface CreateCategoryResponse extends ApiResponse {
	data: Category
}
const CreateCategory = {
	method: "POST" as const,
	url: () => `categories/create`
}

// category :: edit category
export interface EditCategoryRequest {
	id: number
	company_id: number | null
	parent_id?: number | null
	title_en?: string
	title_tr?: string
	title_fa?: string
	content_en?: string
	content_tr?: string
	content_fa?: string
	staff_number_prefix?: string
	file_id?: number | null
	image_id?: number | null
	mobile_image_id?: number | null
	background_image_id?: number | null
	mobile_background_image_id?: number | null
	slug: string
	color?: string | null
}
export interface EditCategoryResponse extends ApiResponse {
	data: Category
}
const EditCategory = {
	method: "POST" as const,
	url: () => `categories/edit`
}

// category :: delete category
export interface DeleteCategoryRequest {
	id: number
	company_id?: number
}
export interface DeleteCategoryResponse extends ApiResponse {}
const DeleteCategory = {
	method: "POST" as const,
	url: (id: any) => `categories/delete/${id}`
}

// category :: search categories
export interface SearchCategoriesRequest {
	type?: string
	company_id?: number | null
	parent_id?: number | null
	search?: string | null
	limit?: number | null
	page?: number | null
	order_by?: "updated" | "created"
	direction?: "asc" | "desc"
}
export interface SearchCategoriesResponse extends ApiResponse {
	data: {
		categories: Category[]
		total: number
	}
}
export const SearchCategories = {
	method: "GET" as const,
	url: () => `categories/search`
}

// category :: get category
export interface GetCategoryRequest {}
export interface GetCategoryResponse extends ApiResponse {
	data: Category
}
const GetCategory = {
	method: "GET" as const,
	url: (id: any) => `categories/${id}`
}

export const useCategoryApiUrl = () => ({
	CreateCategory,
	EditCategory,
	DeleteCategory,
	SearchCategories,
	GetCategory
})
